// src/pages/dataInput/Step2Metadata.tsx
import React, { useEffect, useMemo } from "react";
import { cls } from "../theme/theme";
import { SourcePayload, Relationship, Tool } from "./types";

import BusinessFilesField from "./BusinessFilesField";
import RelationshipEditor from "./RelationshipEditor";

export interface Step2Values {
  appName:            string;
  systemName:         string;
  domain:             string;
  entity:             string;
  entityDescription:  string;
  businessContext:    string;
  relationships:      Relationship[];
}

interface Props {
  tool:               Tool;
  source:             SourcePayload;
  /** Lock app-name field when adding a data source to an existing app. */
  lockedAppName?:     string;
  values:             Step2Values;
  validated:          boolean;
  onChange:           (v: Step2Values) => void;
}

export const Step2Metadata: React.FC<Props> = ({
  tool, source, lockedAppName, values, validated, onChange,
}) => {
  const isSubEntities = source.sheetMode === "sub_entities";
  const isSeparate    = source.sheetMode === "separate_entities";

  // Lock app name if provided
  useEffect(() => {
    if (lockedAppName && values.appName !== lockedAppName) {
      onChange({ ...values, appName: lockedAppName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedAppName]);

  const set = <K extends keyof Step2Values>(k: K, v: Step2Values[K]) =>
    onChange({ ...values, [k]: v });

  // Whether to show the entity name + description fields
  // separate_entities → no parent entity (each table is its own)
  const showEntityFields = isSubEntities;

  // Required-marker helper
  const reqOf = (field: keyof Step2Values): boolean => {
    switch (field) {
      case "appName":           return true;
      case "systemName":        return true;
      case "domain":            return true;
      case "businessContext":   return true;
      case "entity":            return showEntityFields;
      case "entityDescription": return showEntityFields;
      default:                  return false;
    }
  };

  const errOf = (field: keyof Step2Values): boolean => {
    if (!validated) return false;
    if (!reqOf(field)) return false;
    return !String(values[field] ?? "").trim();
  };

  // Source-specific informational summary
  const sourceSummary = useMemo(() => {
    switch (source.kind) {
      case "file":
        const included = (source.sheetConfigs ?? []).filter(s => s.included);
        return included.length > 0
          ? `${included.length} sheet${included.length === 1 ? "" : "s"} selected from ${source.files?.length ?? 1} file${(source.files?.length ?? 1) === 1 ? "" : "s"}`
          : `${source.files?.length ?? 0} file${(source.files?.length ?? 0) === 1 ? "" : "s"}`;
      case "ddl":
        return `${source.parsedTables?.length ?? 0} table${(source.parsedTables?.length ?? 0) === 1 ? "" : "s"} from DDL`;
      case "db":
        const dbT = source.dbSource?.tables?.length ?? 0;
        return source.dbSource?.custom_sql
          ? `Custom SQL on ${source.dbSource.connection_name}`
          : `${dbT} table${dbT === 1 ? "" : "s"} from ${source.dbSource?.connection_name}`;
      case "catalog":
        const cT = source.catalogTables?.length ?? 0;
        return `${cT} table${cT === 1 ? "" : "s"} from ${source.catalogSourceLabel}`;
    }
  }, [source]);

  return (
    <div>
      <div className="gw-section-hdr">Application metadata &amp; relationships</div>

      {/* Source recap */}
      <div style={{
        padding: "10px 14px", borderRadius: 10, background: "#dde7f5",
        border: "1px solid #c8d4e0", marginBottom: 18,
        display: "flex", alignItems: "center", gap: 10, fontSize: 12.5,
      }}>
        <span style={{ fontSize: 16 }}>📦</span>
        <span style={{ color: "#1e3a6e", fontWeight: 600 }}>{source.label}</span>
        <span style={{ color: "#445566" }}>· {sourceSummary}</span>
        <span
          className="gw-mode-badge raw"
          style={{ marginLeft: "auto" }}
          title={isSubEntities
            ? "All inputs grouped under one parent entity"
            : "Each input becomes its own entity"}
        >
          {isSubEntities ? "sub-entities" : "separate entities"}
        </span>
      </div>

      {/* App + System */}
      <div className="gw-form-grid">
        <div className="gw-form-group">
          <label className="gw-label">
            Application Name {reqOf("appName") && <span style={{ color: "#dc2626" }}>*</span>}
          </label>
          <input
            className={cls("gw-input", errOf("appName") && "err")}
            value={values.appName}
            onChange={e => set("appName", e.target.value)}
            disabled={!!lockedAppName}
            placeholder="my-discovery-app"
          />
          {errOf("appName") && <div className="gw-hint" style={{ color: "#dc2626" }}>Required.</div>}
        </div>
        <div className="gw-form-group">
          <label className="gw-label">
            Source System {reqOf("systemName") && <span style={{ color: "#dc2626" }}>*</span>}
          </label>
          <input
            className={cls("gw-input", errOf("systemName") && "err")}
            value={values.systemName}
            onChange={e => set("systemName", e.target.value)}
            placeholder="SAP_ERP_PROD"
          />
          {errOf("systemName") && <div className="gw-hint" style={{ color: "#dc2626" }}>Required.</div>}
        </div>
      </div>

      {/* Domain + (Entity if sub_entities) */}
      <div className="gw-form-grid">
        <div className="gw-form-group">
          <label className="gw-label">
            Data Domain {reqOf("domain") && <span style={{ color: "#dc2626" }}>*</span>}
          </label>
          <input
            className={cls("gw-input", errOf("domain") && "err")}
            value={values.domain}
            onChange={e => set("domain", e.target.value)}
            placeholder="Sales · Healthcare · Supply Chain"
          />
          {errOf("domain") && <div className="gw-hint" style={{ color: "#dc2626" }}>Required.</div>}
        </div>

        {showEntityFields ? (
          <div className="gw-form-group">
            <label className="gw-label">
              Parent Entity Name {reqOf("entity") && <span style={{ color: "#dc2626" }}>*</span>}
            </label>
            <input
              className={cls("gw-input", errOf("entity") && "err")}
              value={values.entity}
              onChange={e => set("entity", e.target.value)}
              placeholder="OrderModel · CustomerMaster · ProductCatalogue"
            />
            {errOf("entity") && <div className="gw-hint" style={{ color: "#dc2626" }}>Required.</div>}
          </div>
        ) : (
          <div className="gw-form-group">
            <label className="gw-label">Entity Name</label>
            <div style={{
              padding: "8px 11px", border: "1px dashed #c8d4e0",
              borderRadius: 8, background: "#f5f7fa",
              fontSize: 12, color: "#445566", lineHeight: 1.45,
            }}>
              Each table/sheet becomes its own entity. Names are taken from the source.
            </div>
          </div>
        )}
      </div>

      {/* Entity description (only for sub_entities) */}
      {showEntityFields && (
        <div className="gw-form-grid gw-form-grid-1">
          <div className="gw-form-group">
            <label className="gw-label">
              Entity Description {reqOf("entityDescription") && <span style={{ color: "#dc2626" }}>*</span>}
            </label>
            <textarea
              className={cls("gw-textarea", errOf("entityDescription") && "err")}
              rows={2}
              value={values.entityDescription}
              onChange={e => set("entityDescription", e.target.value)}
              placeholder="What does this entity represent? Sent to every LLM agent as context — be specific for better enrichment."
            />
            {errOf("entityDescription") && (
              <div className="gw-hint" style={{ color: "#dc2626" }}>Required.</div>
            )}
          </div>
        </div>
      )}

      {/* Business context */}
      <div className="gw-form-grid gw-form-grid-1">
        <div className="gw-form-group">
          <label className="gw-label">
            Business Context {reqOf("businessContext") && <span style={{ color: "#dc2626" }}>*</span>}
          </label>
          <textarea
            className={cls("gw-textarea", errOf("businessContext") && "err")}
            rows={3}
            value={values.businessContext}
            onChange={e => set("businessContext", e.target.value)}
            placeholder="Describe the business domain, purpose, and any relevant context that an LLM agent would need to reason well about this data."
          />
          {errOf("businessContext") && (
            <div className="gw-hint" style={{ color: "#dc2626" }}>Required.</div>
          )}
        </div>
      </div>
      
      {/* Business reference documents */}
      <div style={{ marginTop: 8 }}>
        <BusinessFilesField
          appName={values.appName}
          onFilesChange={count => {
            // Optional: you can track this in parent if needed
            // Most callers only care whether files exist, not the exact list —
            // the backend reads them by app_nm at pipeline time.
          }}
        />
      </div>

      {/* Relationships */}
      <div style={{ marginTop: 16 }}>
        <RelationshipEditor
          availableTables={source.availableTables}
          relationships={values.relationships}
          onChange={rels => set("relationships", rels)}
          files={source.kind === "file" ? source.files : undefined}
          sheetConfigs={source.kind === "file" ? source.sheetConfigs : undefined}
          enableAutoSuggest={source.kind === "file"}
        />
      </div>
    </div>
  );
};

export default Step2Metadata;